import { BuyDto } from "@1games-ru/region-shared/dist/backend/requests/shop";
import { useMutation } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import GameMpProvider from "@/core/data-provider/data-mp/game/game-mp-provider";
import ShopMpProvider from "@/core/data-provider/data-mp/shop/shop-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import { clearCart } from "@/domains/shop/slice";
import toast from "@/domains/toasts/helpers/toast";

type Props = {
  characterId: number;
  data: BuyDto;
};

export default function useShopHandlers() {
  const { t, i18n } = useTranslation("shop");
  const dispatch = useAppDispatch();

  const closeShop = useCallback(() => {
    GameMpProvider.closeCurrentInterface(true);
  }, []);

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ["shop", "buyProducts"],
    mutationFn: async ({ characterId, data }: Props) => {
      const res = await ShopMpProvider.buyProducts(characterId, data);

      if (res instanceof RpcMpProviderSuccessResponse) {
        return res.data;
      }

      const key = res?.error?.message ?? "Unknown";
      const hasTranslation = i18n.exists(`shop:errors.${key}`);

      console.error(res.message);
      throw new Error(hasTranslation ? key : "payError");
    },
    onSuccess: () => {
      dispatch(clearCart());
      toast.success(t("toast.paySuccess"));
    },
    onError: (error) => {
      const messageKey = error?.message ?? "payError";

      if (i18n.exists(`shop:errors.${messageKey}`)) {
        toast.error(t(`errors.${messageKey}`));
      } else {
        toast.error(t("toast.payError"));
      }
    },
    retry: false,
  });

  const buyProducts = useCallback(
    (characterId: number, data: BuyDto) => mutateAsync({ characterId, data }),
    [mutateAsync],
  );

  return {
    closeShop,
    buyProducts,
    isBuying: isPending,
  };
}
